import { useState } from 'react';
import { useLowPower } from '@/utils/lowPower';

interface Star {
  left: number;
  top: number;
  size: number;
  delay: number;
  duration: number;
}

const auroraStyles = `
  @keyframes aurora-bg-star-twinkle {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 0.8; }
  }
  @keyframes aurora-bg-ribbon-1 {
    0%, 100% { transform: translateX(-5%) rotate(-3deg) scaleY(1); }
    50% { transform: translateX(5%) rotate(2deg) scaleY(1.15); }
  }
  @keyframes aurora-bg-ribbon-2 {
    0%, 100% { transform: translateX(5%) rotate(3deg) scaleY(1); }
    50% { transform: translateX(-8%) rotate(-2deg) scaleY(0.9); }
  }
`;

interface AuroraBackgroundProps {
  // Skip stars and animations entirely — for pages where the background
  // is fully covered (e.g. the model builder canvas) and any motion would
  // be wasted GPU work.
  minimal?: boolean;
}

export default function AuroraBackground({ minimal = false }: AuroraBackgroundProps = {}) {
  const detected = useLowPower();
  const lowPower = minimal || detected;

  let starCount = 40;
  if (minimal) starCount = 0;
  else if (lowPower) starCount = 15;
  const [stars] = useState<Star[]>(() =>
    Array.from({ length: starCount }, () => ({
      left: Math.random() * 100, // NOSONAR typescript:S2245 non-crypto-random: decorative star position/size/timing, not a secret
      top: Math.random() * 100, // NOSONAR typescript:S2245 non-crypto-random: decorative star position/size/timing, not a secret
      size: Math.random() * 2 + 1, // NOSONAR typescript:S2245 non-crypto-random: decorative star position/size/timing, not a secret
      delay: Math.random() * 5, // NOSONAR typescript:S2245 non-crypto-random: decorative star position/size/timing, not a secret
      duration: Math.random() * 3 + 2, // NOSONAR typescript:S2245 non-crypto-random: decorative star position/size/timing, not a secret
    }))
  );

  return (
    <>
      {!lowPower && <style>{auroraStyles}</style>}
      <div
        className="fixed inset-0 -z-10 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #040812 0%, #0a1628 40%, #0d1f3c 100%)' }}
      >
        {stars.map((star, i) => (
          <div
            key={i} // NOSONAR typescript:S6479 false-positive: the stars are generated once in useState and never reordered; each is a stateless decorative dot
            className="absolute rounded-full"
            style={{
              left: `${star.left}%`,
              top: `${star.top}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              background: '#fff',
              opacity: 0.3,
              animation: lowPower
                ? undefined
                : `aurora-bg-star-twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
            }}
          />
        ))}
        <div
          className="absolute -top-20 inset-x-0 h-[350px] opacity-30"
          style={{
            background: 'linear-gradient(180deg, transparent, rgb(var(--brew-blue) / 0.12), rgb(var(--brew-blue-light) / 0.06), transparent)',
            borderRadius: '50%',
            filter: 'blur(15px)',
            animation: lowPower ? undefined : 'aurora-bg-ribbon-1 20s ease-in-out infinite',
          }}
        />
        <div
          className="absolute top-10 -left-20 right-20 h-[300px] opacity-20"
          style={{
            background: 'linear-gradient(180deg, transparent, rgba(129, 140, 248, 0.1), rgb(var(--brew-blue) / 0.08), transparent)',
            borderRadius: '50%',
            filter: 'blur(15px)',
            animation: lowPower ? undefined : 'aurora-bg-ribbon-2 25s ease-in-out infinite',
          }}
        />
        <div
          className="absolute bottom-0 inset-x-0 h-48"
          style={{ background: 'radial-gradient(ellipse at 50% 100%, rgb(var(--brew-blue) / 0.06), transparent 70%)' }}
        />
      </div>
    </>
  );
}
